import { Modal } from 'react-bootstrap';
import { TfiClose } from '@react-icons/all-files/tfi/TfiClose';
import { CUSTOM_ACTION } from 'Core-utils/constants/constants';
import CustomActionForm from './components/CustomActionStepperForm';
import AmplifierTrainingStepperForm from './amplifierTraining/AmplifierTrainingStepperForm';
import {
    AmplifierTrainingTemplateKey,
    getAmplifierTrainingTemplateById,
    getAmplifierTrainingTemplateByKey,
    isAmplifierTrainingRestAction,
    isAmplifierTrainingTemplateId,
    parseSelectedSubjectsFromAction,
    parseTrainingTemplateKeyFromAction,
} from './amplifierTraining/amplifierTrainingTemplates';
import { buildAmplifierTrainingRestAction } from './amplifierTraining/buildAmplifierTrainingRestAction';
import { useAmplifierTrainingTemplates } from './amplifierTraining/useAmplifierTrainingTemplates';
import { useComplianceTrainingModules } from './amplifierTraining/useComplianceTrainingModules';
import MicrosoftIntuneRebootStepperForm from './microsoftIntune/MicrosoftIntuneRebootStepperForm';
import {
    MICROSOFT_INTUNE_REBOOT_DESCRIPTION,
    MICROSOFT_INTUNE_REBOOT_TITLE,
    isMicrosoftIntuneRebootActionId,
    isMicrosoftIntuneRebootRestAction,
} from './microsoftIntune/microsoftIntuneTemplates';
import { buildMicrosoftIntuneRebootRestAction } from './microsoftIntune/buildMicrosoftIntuneRebootRestAction';
import { CustomActionDto } from '@ampsec/platform-client';
import { getActionById } from 'Apis/library';
import { useEffect, useState, useCallback } from 'react';
import _ from 'lodash';
import { Loader } from 'Components/molecules';
import classNames from 'classnames';
import './styles.scss';

const MASKED_VALUE = '********';

export interface SensitiveFieldState {
    sensitiveHeaders: string[];
    sensitiveParams: string[];
    maskedHeadersFromApi: Set<string>;
    maskedParamsFromApi: Set<string>;
    localValues: Record<string, string>;
}

interface CustomActionWizardProps {
    show: boolean;
    handleSave: (action: CustomActionDto) => void;
    onClose: () => void;
    id?: string;
}

const CustomActionWizard = ({ show, handleSave, onClose, id }: CustomActionWizardProps) => {
    const [customData, setCustomData] = useState<CustomActionDto>({} as CustomActionDto);
    const [trainingTemplateKey, setTrainingTemplateKey] = useState<AmplifierTrainingTemplateKey | null>(null);
    const [selectedTrainingSubjects, setSelectedTrainingSubjects] = useState<string[]>([]);
    const [isMicrosoftIntuneReboot, setIsMicrosoftIntuneReboot] = useState<boolean>(false);
    const { templates: amplifierTrainingTemplates, loading: templatesLoading } = useAmplifierTrainingTemplates();
    const { modules: complianceTrainingModules } = useComplianceTrainingModules();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [sensitiveState, setSensitiveState] = useState<SensitiveFieldState>({
        sensitiveHeaders: [],
        sensitiveParams: [],
        maskedHeadersFromApi: new Set(),
        maskedParamsFromApi: new Set(),
        localValues: {},
    });

    useEffect(() => {
        if (id) {
            return;
        }

        setIsLoading(false);
        setTrainingTemplateKey(null);
        setSelectedTrainingSubjects([]);
        setIsMicrosoftIntuneReboot(false);
        setCustomData({} as CustomActionDto);
        setSensitiveState({
            sensitiveHeaders: [],
            sensitiveParams: [],
            maskedHeadersFromApi: new Set(),
            maskedParamsFromApi: new Set(),
            localValues: {},
        });
    }, [id]);

    useEffect(() => {
        if (!id || !isMicrosoftIntuneRebootActionId(id)) {
            return;
        }

        setIsMicrosoftIntuneReboot(true);
        setTrainingTemplateKey(null);
        setSelectedTrainingSubjects([]);
        setCustomData({
            displayValue: MICROSOFT_INTUNE_REBOOT_TITLE,
            description: MICROSOFT_INTUNE_REBOOT_DESCRIPTION,
        } as CustomActionDto);
        setIsLoading(false);
    }, [id]);

    useEffect(() => {
        if (!id || !isAmplifierTrainingTemplateId(id)) {
            return;
        }

        if (templatesLoading) {
            setIsLoading(true);
            return;
        }

        const template = getAmplifierTrainingTemplateById(id, amplifierTrainingTemplates);
        setTrainingTemplateKey(template?.key ?? null);
        setSelectedTrainingSubjects([]);
        setCustomData({
            displayValue: template?.title ?? '',
            description: template?.description ?? '',
        } as CustomActionDto);
        setIsLoading(false);
    }, [id, templatesLoading, amplifierTrainingTemplates]);

    useEffect(() => {
        if (!id || isAmplifierTrainingTemplateId(id) || isMicrosoftIntuneRebootActionId(id)) {
            return;
        }

        let cancelled = false;
        setIsLoading(true);

        const fetchData = async () => {
            try {
                const result = await getActionById(id);
                if (cancelled) {
                    return;
                }
                if (!result) {
                    setCustomData({} as CustomActionDto);
                    return;
                }
                const modifiedResult = _.cloneDeep(result);
                if (isAmplifierTrainingRestAction(modifiedResult)) {
                    setTrainingTemplateKey(parseTrainingTemplateKeyFromAction(modifiedResult));
                    setSelectedTrainingSubjects(parseSelectedSubjectsFromAction(modifiedResult));
                    setIsMicrosoftIntuneReboot(false);
                    setCustomData({
                        displayValue: modifiedResult.displayValue ?? '',
                        description: modifiedResult.description ?? '',
                    } as CustomActionDto);
                    return;
                }
                if (isMicrosoftIntuneRebootRestAction(modifiedResult)) {
                    setTrainingTemplateKey(null);
                    setSelectedTrainingSubjects([]);
                    setIsMicrosoftIntuneReboot(true);
                    setCustomData({
                        displayValue: modifiedResult.displayValue ?? '',
                        description: modifiedResult.description ?? '',
                    } as CustomActionDto);
                    return;
                }
                setTrainingTemplateKey(null);
                setSelectedTrainingSubjects([]);
                setIsMicrosoftIntuneReboot(false);
                try {
                    const parsedValue = JSON.parse(modifiedResult?.meta?.request?.payload?.value ?? '');
                    const stringValue = JSON.stringify(parsedValue, null, 2);
                    _.set(modifiedResult ?? {}, 'meta.request.payload.value', stringValue);
                } catch (error) {}

                // Track sensitive fields from API response
                const apiSensitiveHeaders = modifiedResult?.meta?.request?.sensitiveHeaders || [];
                const apiSensitiveParams = modifiedResult?.meta?.request?.sensitiveParams || [];
                const maskedHeadersFromApi = new Set<string>();
                const maskedParamsFromApi = new Set<string>();

                // Identify which headers came back masked
                for (const headerKey of apiSensitiveHeaders) {
                    const value = modifiedResult?.meta?.request?.headers?.[headerKey];
                    if (value === MASKED_VALUE) {
                        maskedHeadersFromApi.add(headerKey);
                    }
                }

                // Identify which params came back masked
                for (const paramKey of apiSensitiveParams) {
                    const value = modifiedResult?.meta?.request?.params?.[paramKey];
                    if (value === MASKED_VALUE) {
                        maskedParamsFromApi.add(paramKey);
                    }
                }
                setSensitiveState({
                    sensitiveHeaders: apiSensitiveHeaders,
                    sensitiveParams: apiSensitiveParams,
                    maskedHeadersFromApi,
                    maskedParamsFromApi,
                    localValues: {},
                });
                setCustomData({ ...modifiedResult } as CustomActionDto);
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        void fetchData();

        return () => {
            cancelled = true;
        };
    }, [id]);

    useEffect(() => {
        if (show) {
            return;
        }
        setTrainingTemplateKey(null);
        setSelectedTrainingSubjects([]);
        setIsMicrosoftIntuneReboot(false);
        setCustomData({} as CustomActionDto);
        setSensitiveState({
            sensitiveHeaders: [],
            sensitiveParams: [],
            maskedHeadersFromApi: new Set(),
            maskedParamsFromApi: new Set(),
            localValues: {},
        });
    }, [show]);

    const handleActionsSave = () => {
        if (trainingTemplateKey) {
            const newAction = buildAmplifierTrainingRestAction({
                displayValue: customData.displayValue,
                description: customData.description,
                templateKey: trainingTemplateKey,
                selectedSubjects: selectedTrainingSubjects,
                modules: complianceTrainingModules,
                retryStrategy: customData.retryStrategy,
            });
            handleSave({ ...newAction, ...(id && !isAmplifierTrainingTemplateId(id) ? { id } : {}) });
            return;
        }

        if (isMicrosoftIntuneReboot) {
            const newAction = buildMicrosoftIntuneRebootRestAction({
                displayValue: customData.displayValue,
                description: customData.description,
                retryStrategy: customData.retryStrategy,
            });
            handleSave({ ...newAction, ...(id && !isMicrosoftIntuneRebootActionId(id) ? { id } : {}) });
            return;
        }

        const newAction: CustomActionDto = _.cloneDeep(customData);

        // Restore actual values for locally-marked sensitive fields (values are masked in UI but we have originals)
        for (const [localKey, value] of Object.entries(sensitiveState.localValues)) {
            const [type, fieldKey] = localKey.split('.');
            if (type === 'headers') {
                _.set(newAction, `meta.request.headers.${fieldKey}`, value);
            } else if (type === 'params') {
                _.set(newAction, `meta.request.params.${fieldKey}`, value);
            }
        }

        // For headers that came back masked from API and weren't re-entered, exclude them
        for (const headerKey of sensitiveState.maskedHeadersFromApi) {
            const currentValue = newAction.meta?.request?.headers?.[headerKey];
            if (currentValue === MASKED_VALUE) {
                _.unset(newAction, `meta.request.headers.${headerKey}`);
            }
        }

        // For params that came back masked from API and weren't re-entered, exclude them
        for (const paramKey of sensitiveState.maskedParamsFromApi) {
            const currentValue = newAction.meta?.request?.params?.[paramKey];
            if (currentValue === MASKED_VALUE) {
                _.unset(newAction, `meta.request.params.${paramKey}`);
            }
        }

        // Include sensitive field arrays in the payload
        _.set(newAction, 'meta.request.sensitiveHeaders', sensitiveState.sensitiveHeaders);
        _.set(newAction, 'meta.request.sensitiveParams', sensitiveState.sensitiveParams);

        handleSave(newAction);
        if (!id) {
            setCustomData({} as CustomActionDto);
            setSensitiveState({
                sensitiveHeaders: [],
                sensitiveParams: [],
                maskedHeadersFromApi: new Set(),
                maskedParamsFromApi: new Set(),
                localValues: {},
            });
        }
    };

    const handleOnClose = () => {
        onClose();
        setCustomData({} as CustomActionDto);
        setTrainingTemplateKey(null);
        setSelectedTrainingSubjects([]);
        setIsMicrosoftIntuneReboot(false);
        setSensitiveState({
            sensitiveHeaders: [],
            sensitiveParams: [],
            maskedHeadersFromApi: new Set(),
            maskedParamsFromApi: new Set(),
            localValues: {},
        });
    };

    /**
     * Handles toggling a field's sensitive status.
     * - For new fields: toggles between sensitive/not sensitive, storing/restoring actual value
     * - For API-masked fields: can only toggle OFF (cannot re-enable without knowing the value)
     */
    const handleToggleSensitive = useCallback(
        (dataKey: 'headers' | 'parameters', fieldKey: string) => {
            const apiKey = dataKey === 'parameters' ? 'params' : dataKey;
            const sensitiveArrayKey = dataKey === 'parameters' ? 'sensitiveParams' : 'sensitiveHeaders';
            const maskedFromApiKey = dataKey === 'parameters' ? 'maskedParamsFromApi' : 'maskedHeadersFromApi';
            const localValueKey = `${apiKey}.${fieldKey}`;

            const isSensitive = sensitiveState[sensitiveArrayKey].includes(fieldKey);
            const isMaskedFromApi = sensitiveState[maskedFromApiKey].has(fieldKey);

            if (isSensitive) {
                // Toggling OFF - remove from sensitive fields
                const newSensitiveArray = sensitiveState[sensitiveArrayKey].filter((k) => k !== fieldKey);

                if (!isMaskedFromApi && sensitiveState.localValues[localValueKey] !== undefined) {
                    // Restore the actual value to the form
                    const actualValue = sensitiveState.localValues[localValueKey];
                    const { [localValueKey]: _removed, ...restLocalValues } = sensitiveState.localValues;

                    setCustomData((prevData) => {
                        const newData = _.cloneDeep(prevData);
                        _.set(newData, `meta.request.${apiKey}.${fieldKey}`, actualValue);
                        return newData;
                    });

                    setSensitiveState((prev) => ({
                        ...prev,
                        [sensitiveArrayKey]: newSensitiveArray,
                        localValues: restLocalValues,
                    }));
                } else {
                    setSensitiveState((prev) => ({
                        ...prev,
                        [sensitiveArrayKey]: newSensitiveArray,
                    }));
                }
            } else {
                // Toggling ON - add to sensitive fields - Only allowed if not already masked from API
                if (!isMaskedFromApi) {
                    const currentValue = _.get(customData, `meta.request.${apiKey}.${fieldKey}`) || '';

                    // Mask the value in the form display
                    setCustomData((prevData) => {
                        const newData = _.cloneDeep(prevData);
                        _.set(newData, `meta.request.${apiKey}.${fieldKey}`, MASKED_VALUE);
                        return newData;
                    });

                    setSensitiveState((prev) => ({
                        ...prev,
                        [sensitiveArrayKey]: [...prev[sensitiveArrayKey], fieldKey],
                        localValues: {
                            ...prev.localValues,
                            [localValueKey]: currentValue,
                        },
                    }));
                }
            }
        },
        [sensitiveState, customData],
    );

    const handleFieldChange = (field: string, value: string) => {
        switch (field) {
            case 'url':
            case 'method':
            case 'headers':
            case 'parameters':
                setCustomData((prevData: CustomActionDto) => {
                    return _.set(_.cloneDeep(prevData), `meta.request.${field}`, value);
                });
                break;
            case 'payload':
                setCustomData((prevData: CustomActionDto) => {
                    return _.set(_.cloneDeep(prevData), `meta.request.payload.format`, value);
                });
                break;
            case 'textAreaValue':
                setCustomData((prevData: CustomActionDto) => {
                    return _.set(_.cloneDeep(prevData), `meta.request.payload.value`, value);
                });
                break;
            case 'displayValue':
            case 'description':
                setCustomData((prevData: CustomActionDto) => ({ ...prevData, [field]: value }));
                break;
            default:
                break;
        }
    };

    const handleTableChange =
        (dataKey: 'headers' | 'parameters', index: number, field: string) =>
        (value: string | React.ChangeEvent<HTMLTextAreaElement>) => {
            const updatedValue = typeof value === 'string' ? value : value.target.value;
            const apiKey = dataKey === 'parameters' ? 'params' : dataKey;
            const sensitiveArrayKey = dataKey === 'parameters' ? 'sensitiveParams' : 'sensitiveHeaders';
            const maskedFromApiKey = dataKey === 'parameters' ? 'maskedParamsFromApi' : 'maskedHeadersFromApi';
            const currentData =
                dataKey === 'headers' ? customData.meta?.request?.headers : customData.meta?.request?.params;
            const entries = Object.entries(currentData || {});
            const currentKey = entries[index]?.[0] || '';

            // If editing a key, we need to handle sensitive field updates
            if (field === 'key') {
                const oldLocalValueKey = `${apiKey}.${currentKey}`;
                const newLocalValueKey = `${apiKey}.${updatedValue}`;

                // Check if the new key would be a duplicate (exists at a different index)
                const existingKeyIndex = entries.findIndex(([key], i) => key === updatedValue && i !== index);
                const wouldBeDuplicate = existingKeyIndex !== -1 && updatedValue !== '';

                // Update sensitive state if this field was sensitive
                setSensitiveState((prev) => {
                    if (prev[sensitiveArrayKey].includes(currentKey)) {
                        // If this would create a duplicate, remove sensitive designation for the target key
                        // This allows the duplicated row's value field to be editable
                        if (wouldBeDuplicate) {
                            const newSensitiveArray = prev[sensitiveArrayKey].filter(
                                (k) => k !== currentKey && k !== updatedValue,
                            );
                            const newMaskedFromApi = new Set(prev[maskedFromApiKey]);
                            newMaskedFromApi.delete(currentKey);
                            newMaskedFromApi.delete(updatedValue);
                            const newLocalValues = { ...prev.localValues };
                            delete newLocalValues[oldLocalValueKey];
                            delete newLocalValues[newLocalValueKey];
                            return {
                                ...prev,
                                [sensitiveArrayKey]: newSensitiveArray,
                                [maskedFromApiKey]: newMaskedFromApi,
                                localValues: newLocalValues,
                            };
                        }

                        // Normal case: rename the sensitive field
                        const newSensitiveArray = prev[sensitiveArrayKey].map((k) =>
                            k === currentKey ? updatedValue : k,
                        );
                        const newMaskedFromApi = new Set(prev[maskedFromApiKey]);
                        if (newMaskedFromApi.has(currentKey)) {
                            newMaskedFromApi.delete(currentKey);
                            newMaskedFromApi.add(updatedValue);
                        }
                        const newLocalValues = { ...prev.localValues };
                        if (prev.localValues[oldLocalValueKey] !== undefined) {
                            newLocalValues[newLocalValueKey] = prev.localValues[oldLocalValueKey];
                            delete newLocalValues[oldLocalValueKey];
                        }
                        return {
                            ...prev,
                            [sensitiveArrayKey]: newSensitiveArray,
                            [maskedFromApiKey]: newMaskedFromApi,
                            localValues: newLocalValues,
                        };
                    } else if (wouldBeDuplicate && prev[sensitiveArrayKey].includes(updatedValue)) {
                        // The target key (that will be overwritten) is sensitive - remove its designation
                        const newSensitiveArray = prev[sensitiveArrayKey].filter((k) => k !== updatedValue);
                        const newMaskedFromApi = new Set(prev[maskedFromApiKey]);
                        newMaskedFromApi.delete(updatedValue);
                        const newLocalValues = { ...prev.localValues };
                        delete newLocalValues[newLocalValueKey];
                        return {
                            ...prev,
                            [sensitiveArrayKey]: newSensitiveArray,
                            [maskedFromApiKey]: newMaskedFromApi,
                            localValues: newLocalValues,
                        };
                    }
                    return prev;
                });
            }

            // If editing a value on a sensitive field that was locally marked (not from API),
            // update the local value store instead of showing it
            if (field === 'value') {
                const localValueKey = `${apiKey}.${currentKey}`;
                const isSensitive = sensitiveState[sensitiveArrayKey].includes(currentKey);
                const isMaskedFromApi = sensitiveState[maskedFromApiKey].has(currentKey);

                if (isSensitive && !isMaskedFromApi) {
                    // Update the local value store, keep masked display
                    setSensitiveState((prev) => ({
                        ...prev,
                        localValues: {
                            ...prev.localValues,
                            [localValueKey]: updatedValue,
                        },
                    }));
                    return; // Don't update the visible form value
                }

                // If editing a masked-from-API field, user is re-entering - remove from maskedFromApi
                if (isMaskedFromApi && updatedValue !== MASKED_VALUE) {
                    setSensitiveState((prev) => {
                        const newMaskedFromApi = new Set(prev[maskedFromApiKey]);
                        newMaskedFromApi.delete(currentKey);
                        return {
                            ...prev,
                            [maskedFromApiKey]: newMaskedFromApi,
                            localValues: {
                                ...prev.localValues,
                                [localValueKey]: updatedValue,
                            },
                        };
                    });
                    // Keep showing masked value, store actual in localValues
                    return;
                }
            }

            setCustomData((prevData: CustomActionDto) => {
                const newData = _.cloneDeep(prevData);
                if (dataKey === 'headers' && !_.isEmpty(newData.meta?.request?.headers)) {
                    _.set(
                        newData,
                        `meta.request.headers`,
                        Object.fromEntries(
                            Object.entries(newData.meta?.request?.headers)?.map(([key, val], currentIndex) =>
                                currentIndex === index
                                    ? [field === 'key' ? updatedValue : key, field === 'value' ? updatedValue : val]
                                    : [key, val],
                            ),
                        ),
                    );
                }
                if (dataKey === 'parameters' && !_.isEmpty(newData.meta?.request?.params)) {
                    _.set(
                        newData,
                        `meta.request.params`,
                        Object.fromEntries(
                            Object.entries(newData.meta?.request?.params).map(([key, val], currentIndex) =>
                                currentIndex === index
                                    ? [field === 'key' ? updatedValue : key, field === 'value' ? updatedValue : val]
                                    : [key, val],
                            ),
                        ),
                    );
                }
                return newData;
            });
        };

    const handleAddRow = (dataKey: 'headers' | 'parameters') => () => {
        setCustomData((prevData: CustomActionDto) => {
            const newData = _.cloneDeep(prevData);
            const newField = '';
            if (dataKey === 'headers') {
                _.set(newData, `meta.request.headers.${newField}`, '');
            } else {
                _.set(newData, `meta.request.params.${newField}`, '');
            }
            return newData;
        });
    };

    const handleDeleteRow = (dataKey: 'headers' | 'parameters') => (index: number) => () => {
        const apiKey = dataKey === 'parameters' ? 'params' : dataKey;
        const sensitiveArrayKey = dataKey === 'parameters' ? 'sensitiveParams' : 'sensitiveHeaders';
        const maskedFromApiKey = dataKey === 'parameters' ? 'maskedParamsFromApi' : 'maskedHeadersFromApi';
        const currentData =
            dataKey === 'headers' ? customData.meta?.request?.headers : customData.meta?.request?.params;
        const fieldKey = Object.keys(currentData || {})[index];
        const localValueKey = `${apiKey}.${fieldKey}`;

        // Clean up sensitive state for deleted field
        setSensitiveState((prev) => {
            const newSensitiveArray = prev[sensitiveArrayKey].filter((k) => k !== fieldKey);
            const newMaskedFromApi = new Set(prev[maskedFromApiKey]);
            newMaskedFromApi.delete(fieldKey);
            const { [localValueKey]: _removed, ...newLocalValues } = prev.localValues;
            return {
                ...prev,
                [sensitiveArrayKey]: newSensitiveArray,
                [maskedFromApiKey]: newMaskedFromApi,
                localValues: newLocalValues,
            };
        });

        setCustomData((prevData: CustomActionDto) => {
            const newData = { ...prevData.meta?.request };
            if (dataKey === 'headers' && newData.headers) {
                delete newData.headers[Object.keys(newData.headers)[index]];
            }
            if (dataKey === 'parameters' && newData.params) {
                delete newData.params[Object.keys(newData.params)[index]];
            }
            return { ...prevData, meta: { ...prevData.meta, request: newData } };
        });
    };

    const modalBodyClassName = classNames(
        'bg-body-color text-text-high-emphasis rounded-bottom border border-structural-stroke-100 border-top-0',
        {
            'd-flex justify-content-center': isLoading,
            'custom-action-wizard__body--training':
                Boolean(trainingTemplateKey || isMicrosoftIntuneReboot) && !isLoading,
        },
    );

    return (
        <Modal
            show={show}
            centered
            size="lg"
            className="custom-action-wizard"
            data-testid="custom-action-wizard"
            onHide={() => {
                if (show) {
                    handleOnClose();
                }
            }}
        >
            <Modal.Header className="custom-action-wizard__header bg-body-color text-text-high-emphasis border border-structural-stroke-100 rounded-top">
                <Modal.Title className="subtitle1 p-1 text-uppercase">
                    {trainingTemplateKey
                        ? (getAmplifierTrainingTemplateByKey(trainingTemplateKey, amplifierTrainingTemplates)?.title ??
                          CUSTOM_ACTION)
                        : isMicrosoftIntuneReboot
                          ? MICROSOFT_INTUNE_REBOOT_TITLE
                          : CUSTOM_ACTION}
                </Modal.Title>
                <TfiClose
                    className="text-text-high-emphasis btn-close position-absolute end-0 me-2"
                    onClick={handleOnClose}
                    size={10}
                    data-testid="btn-close"
                />
            </Modal.Header>
            <Modal.Body className={modalBodyClassName}>
                {isLoading ? (
                    <Loader />
                ) : trainingTemplateKey ? (
                    <AmplifierTrainingStepperForm
                        templateKey={trainingTemplateKey}
                        templates={amplifierTrainingTemplates}
                        data={customData}
                        selectedSubjects={selectedTrainingSubjects}
                        onSelectedSubjectsChange={setSelectedTrainingSubjects}
                        handleFieldChange={handleFieldChange}
                        handleSave={handleActionsSave}
                    />
                ) : isMicrosoftIntuneReboot ? (
                    <MicrosoftIntuneRebootStepperForm
                        data={customData}
                        handleFieldChange={handleFieldChange}
                        handleSave={handleActionsSave}
                    />
                ) : (
                    <CustomActionForm
                        handleSave={handleActionsSave}
                        data={customData}
                        handleFieldChange={handleFieldChange}
                        handleAddRow={handleAddRow}
                        handleDeleteRow={handleDeleteRow}
                        handleTableChange={handleTableChange}
                        sensitiveState={sensitiveState}
                        handleToggleSensitive={handleToggleSensitive}
                    />
                )}
            </Modal.Body>
        </Modal>
    );
};

export default CustomActionWizard;
